import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { notificationAPI } from '../services/api'

interface BellItem {
  org_nr: string
  org_navn: string
  type: 'eie' | 'leasing' | 'solgt' | 'kjopt'
  dag: string
  antall: number
  unread: boolean
}

interface BellPayload {
  unread_count: number
  items: BellItem[]
}

/**
 * Notification bell shown in the header for users who have configured
 * `mine_organisasjoner`. Refetches on route change (no polling). When the
 * user first opens the dropdown in a page view, POSTs /notifications/ack
 * for every unread item currently visible — acks persist in
 * user_hendelse_ack so items stay read across sessions and devices.
 */
export default function NotificationBell() {
  const { t } = useTranslation('components')
  const { user } = useAuth()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const [payload, setPayload] = useState<BellPayload>({ unread_count: 0, items: [] })
  const hasAckedRef = useRef(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const hasWatchedOrgs =
    typeof user?.mine_organisasjoner === 'string' && user.mine_organisasjoner.trim().length > 0

  // Refetch on route change
  useEffect(() => {
    if (!hasWatchedOrgs) return
    let cancelled = false
    notificationAPI
      .getBell()
      .then((res) => {
        if (cancelled) return
        const data = res.data as BellPayload
        setPayload({
          unread_count: data?.unread_count ?? 0,
          items: Array.isArray(data?.items) ? data.items : [],
        })
        // Reset ack guard when route changes so a new pageview can ack again
        hasAckedRef.current = false
      })
      .catch(() => {
        if (cancelled) return
        setPayload({ unread_count: 0, items: [] })
      })
    return () => {
      cancelled = true
    }
     
  }, [location.pathname, hasWatchedOrgs])

  // Close on outside click / Escape
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [isOpen])

  if (!hasWatchedOrgs) return null

  const handleToggle = () => {
    const next = !isOpen
    setIsOpen(next)
    if (next && !hasAckedRef.current) {
      hasAckedRef.current = true
      const toAck = payload.items
        .filter((i) => i.unread)
        .map((i) => ({ org_nr: i.org_nr, dag: i.dag, type: i.type }))
      if (toAck.length > 0) {
        // Fire-and-forget
        notificationAPI.ack(toAck).catch(() => {
          // Swallow — ack is best-effort; next fetch will resurface if needed
        })
      }
      // Optimistically mark currently-unread items as read
      setPayload((prev) => ({
        unread_count: 0,
        items: prev.items.map((i) => (i.unread ? { ...i, unread: false } : i)),
      }))
    }
  }

  const unread = payload.unread_count

  return (
    <div className="relative" ref={containerRef} data-testid="notification-bell-wrapper">
      <button
        type="button"
        onClick={handleToggle}
        aria-label={t('notificationBell.title')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        data-testid="notification-bell"
        className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-card transition-colors"
      >
        <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        {unread > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute top-1 right-1 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold leading-none"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          role="menu"
          data-testid="notification-bell-menu"
          className="absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-30"
        >
          <div className="py-2 max-h-96 overflow-y-auto">
            <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('notificationBell.title')}</p>
            </div>
            {payload.items.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                {t('notificationBell.empty')}
              </div>
            ) : (
              payload.items.map((item, idx) => {
                const href =
                  `/hendelsessenter?org=${encodeURIComponent(item.org_nr)}` +
                  `&dag=${encodeURIComponent(item.dag)}` +
                  `&type=${encodeURIComponent(item.type)}`
                return (
                  <Link
                    key={`${item.org_nr}-${item.dag}-${item.type}-${idx}`}
                    to={href}
                    data-testid={`notification-bell-item-${item.org_nr}`}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      item.unread
                        ? 'text-gray-900 dark:text-gray-100'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                    role="menuitem"
                  >
                    <div className="font-medium flex items-center gap-2">
                      {item.unread && (
                        <span
                          aria-hidden
                          className="inline-block w-2 h-2 rounded-full bg-red-600"
                        />
                      )}
                      <span>{item.org_navn}</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t('notificationBell.itemLine', {
                        n: item.antall,
                        type:
                          item.type === 'leasing'
                            ? t('notificationBell.leasing')
                            : item.type === 'solgt'
                            ? t('notificationBell.solgt')
                            : item.type === 'kjopt'
                            ? t('notificationBell.kjopt')
                            : t('notificationBell.ownership'),
                        dag: item.dag,
                      })}
                    </div>
                  </Link>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
